import React, { useState, useEffect, Suspense, lazy } from 'react';
import { motion } from 'framer-motion';

// Lazy load components for better performance
const Header = lazy(() => import('./components/Header'));
const Hero = lazy(() => import('./components/Hero'));
const About = lazy(() => import('./components/About'));
const Skills = lazy(() => import('./components/Skills'));
const TechStackHeatmap = lazy(() => import('./components/TechStackHeatmap'));
const Experience = lazy(() => import('./components/Experience'));
const Projects = lazy(() => import('./components/Projects'));
const Certifications = lazy(() => import('./components/Certifications'));
const Contact = lazy(() => import('./components/Contact'));
const Footer = lazy(() => import('./components/Footer'));

// Loading placeholder component
const LoadingPlaceholder = () => (
  <div className="min-h-screen flex items-center justify-center bg-black">
    <div className="animate-pulse text-purple-400">Loading...</div>
  </div>
);

// Create theme context
const ThemeContext = React.createContext({
  isDarkMode: true, // Default to dark mode
  toggleTheme: () => {},
});

// Create app context for active section
const AppContext = React.createContext({
  activeSection: 'hero',
  setActiveSection: (section: string) => {},
});

function App() {
  const [isDarkMode, setIsDarkMode] = useState(true);
  const [activeSection, setActiveSection] = useState('hero');
  const [isLoading, setIsLoading] = useState(true);

  // Toggle theme function
  const toggleTheme = () => {
    setIsDarkMode(!isDarkMode);
  };

  // Apply theme class to body
  useEffect(() => {
    if (isDarkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, [isDarkMode]);

  // Detect active section on scroll
  useEffect(() => {
    const handleScroll = () => {
      const sections = document.querySelectorAll('section[id]');
      const scrollPosition = window.scrollY + 100;
      
      sections.forEach(section => {
        const sectionTop = (section as HTMLElement).offsetTop;
        const sectionHeight = section.clientHeight;
        const sectionId = section.getAttribute('id') || '';
        
        if (scrollPosition >= sectionTop && scrollPosition < sectionTop + sectionHeight) {
          setActiveSection(sectionId);
        }
      });
    };
    
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Simulate loading
  useEffect(() => {
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 2000);
    
    return () => clearTimeout(timer);
  }, []);

  // Loading screen
  if (isLoading) {
    return (
      <div className="fixed inset-0 bg-black flex items-center justify-center z-50">
        <div className="text-center">
          <motion.div
            className="text-4xl md:text-6xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-blue-400 mb-4"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            Estifanos Wondimu
          </motion.div>
          
          <motion.div
            className="w-64 h-1 bg-gradient-to-r from-purple-500 to-blue-500 mx-auto mb-8"
            initial={{ width: 0, opacity: 0 }}
            animate={{ width: '16rem', opacity: 1 }}
            transition={{ duration: 1, delay: 0.4 }}
          />
          
          <motion.div
            className="text-gray-400"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.8 }}
          >
            <div className="w-12 h-12 rounded-full border-t-2 border-l-2 border-purple-500 mx-auto animate-spin"></div>
          </motion.div>
        </div>
      </div>
    );
  }

  return (
    <ThemeContext.Provider value={{ isDarkMode, toggleTheme }}>
      <AppContext.Provider value={{ activeSection, setActiveSection }}>
        <div className="min-h-screen bg-black text-white relative overflow-hidden">
          {/* Background elements */}
          <div className="fixed inset-0 bg-grid-pattern opacity-20"></div>
          
          {/* Main content with Suspense for code splitting */}
          <Suspense fallback={<LoadingPlaceholder />}>
            <Header />
            
            <main>
              <Suspense fallback={null}>
                <Hero />
                <About />
                <Skills />
                <TechStackHeatmap />
                <Experience />
                <Projects />
                <Certifications />
                <Contact />
              </Suspense>
            </main>
            
            <Footer />
          </Suspense>
          
          {/* Neon border effect */}
          <div className="fixed inset-0 pointer-events-none border-2 border-purple-500/10 z-50"></div>
          <div className="fixed inset-0 pointer-events-none border border-blue-500/5 z-50"></div>
        </div>
      </AppContext.Provider>
    </ThemeContext.Provider>
  );
}

export default App;
export { ThemeContext, AppContext };
